import React, { useEffect, useState, useCallback } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Building2, ShieldCheck, Megaphone, Calendar, Users, MessageSquare, FileText, Mail, Smartphone, UserCog, Wallet, Percent, Briefcase, XCircle, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { key: "listings", to: "/admin/listings", label: "Listings", icon: Building2 },
  { key: "kyc", to: "/admin/kyc", label: "KYC", icon: ShieldCheck },
  { key: "ads", to: "/admin/ads", label: "Ads", icon: Megaphone },
  { key: "bookings", to: "/admin/bookings", label: "Bookings", icon: Calendar },
  { key: "guests", to: "/admin/guests", label: "Guests", icon: Users },
  { key: "hosts", to: "/admin/hosts", label: "Hosts", icon: Percent },
  { key: "businesses", to: "/admin/businesses", label: "Businesses", icon: Briefcase },
  { key: "messages", to: "/admin/messages", label: "Messages", icon: Mail },
  { key: "twilio", to: "/admin/twilio", label: "SMS", icon: MessageSquare },
  { key: "payments", to: "/admin/payments", label: "Payments", icon: Smartphone },
  { key: "payouts", to: "/admin/payouts", label: "Payouts", icon: Wallet },
  { key: "manager_subs", to: "/admin/manager-subscriptions", label: "Manager Subs", icon: Sparkles },
  { key: "terms", to: "/admin/terms", label: "Terms", icon: FileText },
  { key: "team", to: "/admin/team", label: "Team", icon: UserCog, superOnly: true },
];

export default function AdminLayout() {
  const { user } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [pendingListings, setPendingListings] = useState(0);

  // Auto-redirect /admin → the first tab this admin can actually access. Super
  // admins land on /admin/listings (unchanged). Scoped admins land on the
  // first tab they have permission for so the URL always matches a visible tab.
  useEffect(() => {
    if (loc.pathname === "/admin" || loc.pathname === "/admin/") {
      const roles = user?.admin_roles || [];
      const hasFull = user?.is_super_admin || roles.includes("full");
      let target = "/admin/listings";
      if (!hasFull) {
        const firstAccessible = TABS.find(
          (t) => !t.superOnly && roles.includes(t.key)
        );
        if (firstAccessible) target = firstAccessible.to;
      }
      nav(target, { replace: true });
    }
  }, [loc.pathname, nav, user]);

  // Poll the new-listings alert badge every 60s while an admin is on any admin tab.
  const loadPending = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/properties/counts`);
      setPendingListings(data.pending || 0);
    } catch { /* silent — non-critical badge */ }
  }, []);
  useEffect(() => {
    if (!user?.is_admin) return;
    loadPending();
    const t = setInterval(loadPending, 60000);
    return () => clearInterval(t);
  }, [user, loadPending]);

  if (!user) {
    return <div className="mx-auto max-w-2xl px-6 py-16 text-center text-slate-500">Please sign in.</div>;
  }
  if (!user.is_admin) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-16 text-center">
        <XCircle className="mx-auto h-12 w-12 text-rose-500" />
        <h1 className="mt-4 font-display text-2xl font-bold">Admin only</h1>
        <p className="mt-2 text-slate-600">You don&apos;t have access to this section.</p>
      </div>
    );
  }

  // Filter visible tabs based on this admin's roles. Super admins + admins with
  // the "full" role see every tab. Section-scoped admins only see the tabs
  // they've been granted. The "Team" tab is restricted to the super admin.
  const roles = user.admin_roles || [];
  const hasFullAccess = user.is_super_admin || roles.includes("full");
  const visibleTabs = TABS.filter((t) => {
    if (t.superOnly) return !!user.is_super_admin;
    if (hasFullAccess) return true;
    return roles.includes(t.key);
  });

  return (
    <div data-testid="admin-layout">
      {/* Tab bar */}
      <div className="sticky top-[57px] z-30 border-b border-slate-200 bg-white/90 backdrop-blur">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-1 overflow-x-auto py-2">
            <span className="mr-3 flex-shrink-0 rounded-full bg-forest px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-white">Admin</span>
            {visibleTabs.map((t) => (
              <NavLink
                key={t.key}
                to={t.to}
                data-testid={`admin-tab-${t.key}`}
                className={({ isActive }) =>
                  `flex flex-shrink-0 items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-semibold transition ${
                    isActive
                      ? "bg-copper text-white shadow-sm"
                      : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                  }`
                }
              >
                <t.icon className="h-4 w-4" />
                {t.label}
                {t.key === "listings" && pendingListings > 0 && (
                  <span data-testid="listings-pending-badge"
                    className="ml-1 rounded-full bg-rose-600 px-1.5 py-0.5 text-[10px] font-bold text-white">
                    {pendingListings}
                  </span>
                )}
              </NavLink>
            ))}
          </div>
        </div>
      </div>

      <Outlet context={{ refreshPendingListings: loadPending }} />
    </div>
  );
}
